// 代理后台业务 hook 集合（详见 docs/api需求/agent/全套.md）
//
// 后端 chi 路由：/gapi/agent/*（需 agent role）

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, getSupabase, type DayPoint } from '@dogdisk/shared'

export type AgentDashboard = {
  kpi: {
    current_balance_coin: number
    balance_in_cny: number
    month_commission_coin: number
    total_commission_coin: number
    my_users_count: number
  }
  recent_transactions: AgentTxRow[]
  trend_30d?: Array<{ date: string; commission_coin: number }>
}

export type AgentTxRow = {
  id: string
  reference_id: string
  reference_type: string
  from_user_username_masked: string
  amount_coin: number
  level: 'l1' | 'l2' | 'l3' | string
  status: string
  created_at: string
}

export type SubUserRow = {
  /** 明文 UUID — drawer (fiJdB) `.eq('user_id', X)` 精确过滤用;owner-only RLS 保护 */
  user_id: string
  /** 脱敏短码 (前 4 + ***) — UI 列表 / drawer header 展示用 */
  user_id_masked: string
  username_masked: string
  /** 昵称(profiles.nickname,NOT NULL) — wWJ6V 昵称列 */
  nickname: string
  avatar_url: string | null
  phone_last4: string | null
  registered_at: string
  vip_tier: string | null
  total_recharge_cny_cents: number
  commission_from_user_coin: number
  level: 'l1' | 'l2' | 'l3' | string
}

export type ListMyUsersOutput = {
  users: SubUserRow[]
  total: number
}

export type PromoNeighbor = {
  user_id_masked: string
  /** 完整 uuid：仅 sub_agents 返回（代理设置页 B7KvU 展示 + 调分成比例用） */
  user_id?: string
  username_masked: string
  rate_bps?: number | null
  level: number
  /** 该下级代理拉到的一级用户数（下级数）：仅 sub_agents 返回 */
  first_agent_count?: number
}

export type PromoInfo = {
  promo_url: string
  level: number
  rate_bps: number
  parent_agent: PromoNeighbor | null
  sub_agents: PromoNeighbor[]
  share_templates: string[]
}

export function useAgentDashboard() {
  return useQuery({
    queryKey: ['agent', 'dashboard'] as const,
    queryFn: () => apiClient.get<AgentDashboard>('/gapi/agent/dashboard'),
    staleTime: 30_000,
  })
}

export function useMyUsers(level: 'l1' | 'l2' | 'l3' | 'all' = 'all', limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['agent', 'users', level, limit, offset] as const,
    queryFn: () =>
      apiClient.get<ListMyUsersOutput>('/gapi/agent/users', {
        query: { level, limit, offset },
      }),
    staleTime: 30_000,
  })
}

export function useMyTransactions(limit = 100) {
  return useQuery({
    queryKey: ['agent', 'transactions', limit] as const,
    queryFn: () =>
      apiClient.get<{ transactions: AgentTxRow[] }>('/gapi/agent/transactions', {
        query: { limit },
      }),
    staleTime: 30_000,
  })
}

export function usePromoInfo() {
  return useQuery({
    queryKey: ['agent', 'promo'] as const,
    queryFn: () => apiClient.get<PromoInfo>('/gapi/agent/promo'),
    staleTime: 60_000,
  })
}

// ──────────────────── 钱包提现 ────────────────────
//
// 后端：/gapi/wallet/* （SafeWalletModule 嵌入式 router） + /gapi/withdraw/*

export type WalletBalance = {
  available_coin: number
  cny_cents: number
  /** 多币种概览（USDT / USDC / BTC / CNY） */
  by_currency?: Array<{
    currency: string
    amount: number
    rate_to_cny?: number
  }>
}

export type WithdrawRow = {
  id: string
  /** 业务上仅 COIN；保留 currency 字段是 UI 渲染兼容（旧设计 4 币种 tab） */
  currency: string
  /** UI 显示金额（旧设计 amount；与 amount_coin 同值） */
  amount: number
  amount_coin: number
  payout_method: string
  payout_contact?: string
  status:
    | 'pending'
    | 'awaiting_review'
    | 'approved'
    | 'completed'
    | 'rejected'
    | 'cancelled'
    | string
  reject_reason?: string | null
  created_at: string
  updated_at?: string
}

type WithdrawListResp = {
  count: number
  withdrawals: Array<{
    id: string
    user_id: string
    amount_coin: number
    payout_method: string
    payout_contact: string
    status: string
    reject_reason: string | null
    idempotency_key: string
    created_at: string
    updated_at: string
  }>
}

/** payout_method → UI 展示币种映射（ alipay/wechat/bank_card/other → CNY；usdt/usdc/btc 各自 ） */
function payoutMethodToCurrency(m: string): string {
  switch (m) {
    case 'usdt':
      return 'USDT'
    case 'usdc':
      return 'USDC'
    case 'btc':
      return 'BTC'
    default:
      return 'CNY'
  }
}

/**
 * useWalletBalance — 直接走 PostgREST 查 safewallet.wallet
 *
 * 不调用 /gapi/wallet/balance（SafeWalletModule 嵌入的 router 那个端点是
 * "admin/operator" 视角，要求 X-Operator-Id header — 不适合代理自助）。
 *
 * 用户 SPA 也是同样 PostgREST 路径（详见 user/src/_lib/wallet-api.ts）。
 */
export function useWalletBalance(userId: string | undefined) {
  return useQuery({
    queryKey: ['agent', 'wallet', 'balance', userId] as const,
    enabled: Boolean(userId),
    queryFn: async (): Promise<WalletBalance | null> => {
      if (!userId) return null
      const sb = getSupabase()
      // v_wallet: security_invoker 视图,底表 wallet_select_own RLS 限 agent 自己钱包
      const { data, error } = await sb
        .schema('safewallet')
        .from('v_wallet')
        .select('balance')
        .eq('owner_type', 'user')
        .eq('user_id', userId)
        .eq('currency', 'COIN')
        .maybeSingle()
      if (error) throw error
      const coin = data ? Number(data.balance) : 0
      return {
        available_coin: coin,
        cny_cents: coin, // 100 COIN = 1 元 = 100 cents → 同值
      }
    },
    staleTime: 30_000,
  })
}

// ──────────────────── B6 扩展 endpoint(详见 server/internal/httpapi/agent_extras_handlers.go)────────────────────

export type WithdrawDetail = {
  id: string
  user_id: string
  amount_coin: number
  payout_method: string
  payout_contact?: string
  status: string
  reject_reason: string | null
  idempotency_key: string
  created_at: string
  updated_at: string
}

/** useWithdrawDetail — 提现详情(pen frame t0hY8H) */
export function useWithdrawDetail(withdrawId: string | null) {
  return useQuery({
    queryKey: ['agent', 'withdraw', 'detail', withdrawId] as const,
    enabled: !!withdrawId,
    queryFn: async (): Promise<WithdrawDetail | null> => {
      if (!withdrawId) return null
      return apiClient.get<WithdrawDetail>(`/gapi/agent/withdraw/${withdrawId}`)
    },
    staleTime: 30_000,
  })
}

export type DomainDryrunResult = {
  domain: string
  dns_check: { status: string; message: string }
  cert_check: { status: string; message: string }
  overall_ready: boolean
  estimated_seconds: number
  hints: string[]
}

/** runDomainDryrun — 域名 dry-run(pen frame bkoLR) */
export function runDomainDryrun(domain: string): Promise<DomainDryrunResult> {
  return apiClient.post<DomainDryrunResult>('/gapi/agent/domains/dryrun', { domain })
}

/** phone rebind 3-step api(pen frame PHYaZ) */
export type PhoneRequestOldOTPResult = {
  user_id: string
  masked_phone: string
  sent_at: string
  expires_in: number
  dev_mode: boolean
  note: string
}
export function requestOldPhoneOTP(): Promise<PhoneRequestOldOTPResult> {
  return apiClient.post<PhoneRequestOldOTPResult>('/gapi/agent/phone/request-old-otp', {})
}

export function verifyNewPhoneOTP(input: {
  old_otp: string
  new_phone: string
  new_otp: string
}): Promise<{ user_id: string; new_phone: string; changed: boolean }> {
  return apiClient.post('/gapi/agent/phone/verify-new-otp', input)
}

/** setL3JumpMode — L3 跳转模式(pen frame m7PhzR) */
export function setL3JumpMode(mode: 'on' | 'off'): Promise<{ user_id: string; mode: string }> {
  return apiClient.put('/gapi/agent/l3-mode', { mode })
}

// ──────────────────── 通知中心(复用 user 端 messaging endpoint;pen frame TnT1r)────────────────────

export type ConversationRow = {
  conv_id: string
  ticket_id?: string | null
  partner_user_id?: string | null
  partner_username?: string | null
  partner_avatar?: string | null
  last_message?: string | null
  last_message_at?: string | null
  unread_count?: number
}

export function useMyConversations() {
  return useQuery({
    queryKey: ['agent', 'notifications', 'conversations'] as const,
    queryFn: () =>
      apiClient.get<{ conversations: ConversationRow[] }>('/gapi/agent/conversations'),
    staleTime: 15_000,
  })
}

export function markMessagesRead(conversationId: string) {
  return apiClient.post(`/gapi/agent/conversations/${conversationId}/read`, {})
}

// ──────────────────── B7 批次 hooks(2026-05-21) ────────────────────
//
// 3 新子系统:
//   - 登录历史(loTcH)
//   后端 endpoint 详见 server/internal/httpapi/b7_handlers.go

export type LoginHistoryRow = {
  id: string
  user_id: string
  ip?: string
  ua?: string
  success: boolean
  failure_reason?: string
  created_at: string
}

export function useMyLoginHistory(limit = 30) {
  return useQuery({
    queryKey: ['agent', 'login_history', limit] as const,
    queryFn: () =>
      apiClient.get<{ history: LoginHistoryRow[] }>('/gapi/agent/login-history', {
        query: { limit },
      }),
    staleTime: 30_000,
  })
}

/**
 * useWithdrawHistory — 提现历史
 *
 * 2026-04-30 起 server 已落 public.withdraw 表 + GET /gapi/withdraw/list 端点；
 * 此处直调 gAPI（不走 PostgREST，因 RLS 已限定自己；handler 直接 service_role 查 DB）。
 */
// ──────────────────── B5 批次 hooks(2026-05-23;9 frontend-only frame)────────────────────
//
// 全部走 supabase PostgREST 直读。**所有表 / RPC 暂未落地**:
//   - agent_setting / domain_api_key / agent_domain / login_history /
//     wallet_payout_account 6 张表 全部为 TODO(api) — 当前 fallback 返回空 list,真表上线后
//     RLS 自动按 user_id = auth.uid() 过滤;前端无需改 hook。
//
// 为何不直接复用 B7 的 `useMyLoginHistory` hook?
//   B7 走 /gapi/* server endpoint(handler 已实装但表可能未 migration);B5 仅做 supabase 直读 fallback,
//   以保证 frame 在 backend 未上线时仍能渲染空态(避免 5xx 噪音)。两条路径同时存在:
//   - 短期(本 PR):新 routes 用 B5 supabase 直读 hooks
//   - 长期(B7 backend 完工后):删 B5 hook,改回 B7 server hook

// ─── recommend setting(`CoKKd`)— 已落表 agent_setting ───
export type RecommendSetting = {
  agent_id: string
  recommend_uuids: string[] | null // 推荐用户白名单(UUID v4 数组),null = 关闭
  algorithm_mode: 'A' | 'B' | null // A=白名单 / B=最热回退
  updated_at?: string | null
}

export function useRecommendSetting() {
  return useQuery({
    queryKey: ['agent', 'recommend_setting'] as const,
    queryFn: async (): Promise<RecommendSetting | null> => {
      const sb = getSupabase()
      // TODO(api): agent_setting 表已存在 rate_bps 字段,
      // 但 recommend_uuids / algorithm_mode 列**未落** — 待 schema migration 加列后启用。
      // eslint-disable-next-line no-restricted-syntax -- agent_setting by agent_id 读自己配置(owner RLS)
      const { data, error } = await sb
        .from('agent_setting')
        .select('agent_id, recommend_uuids, algorithm_mode, updated_at')
        .maybeSingle()
      if (error) {
        // 列不存在 → 返回 stub 而非抛错(让 UI 渲染空态而非红屏)
        return { agent_id: '', recommend_uuids: null, algorithm_mode: null }
      }
      return (data as RecommendSetting | null) ?? {
        agent_id: '',
        recommend_uuids: null,
        algorithm_mode: null,
      }
    },
    staleTime: 30_000,
  })
}

export function useUpdateRecommendSetting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { recommend_uuids: string[]; algorithm_mode: 'A' | 'B' }) => {
      const sb = getSupabase()
      const { error } = await sb.from('agent_setting').update(input).select().maybeSingle()
      if (error) throw error
      return { ok: true } as const
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', 'recommend_setting'] }),
  })
}

// ─── domain guard:密钥配置(`ewS3j`)+ 域名配置(`Ye71I`)─────────────────
// B2.6.D 起切真 backend(/gapi/agent_set_dns_credential 等 8 endpoint)
// hook 已迁移至 ./jumpdomain-api.ts:
//   useDnsCredentials / useSetDnsCredential / useTestDnsCredential
//   useJumpChains / useCreateJumpChain / useCreateJumpDomain / useRefreshDomainDNS

// ─── 钱包绑定:USDT(`SlxwM`)+ 银行卡(`i2QnN`)— REST /gapi/payout-accounts ───
//
// 后端实装见:
//   server/internal/walletpayout/service.go(svc 层 + Luhn / regex / 30 天风控 / bcrypt)
//   server/internal/httpapi/payout_account_handlers.go(/gapi/payout-accounts REST endpoint)
//   docs/api-list/agent/wallet-payout-account.md(API 协议)

export type WalletPayoutAccount = {
  id: string
  user_id: string
  type: 'usdt_trc20' | 'usdt_erc20' | 'bank_cn' | string
  address?: string // USDT 公开地址
  bank_name?: string
  holder_name?: string // bank_cn 持卡人
  card_no_last4?: string // bank_cn 末 4 位(展示用)
  is_default: boolean
  status: 'active' | 'inactive' | string
  created_at: string
  updated_at: string
  deleted_at?: string | null
}

export function useMyPayoutAccounts() {
  return useQuery({
    queryKey: ['agent', 'payout_accounts'] as const,
    queryFn: async (): Promise<{ accounts: WalletPayoutAccount[] }> => {
      const resp = await apiClient.get<{ accounts: WalletPayoutAccount[] }>(
        '/gapi/agent/payout-accounts',
      )
      return { accounts: resp.accounts ?? [] }
    },
    staleTime: 30_000,
  })
}

// useBindUsdtAddress P3-W7:绑 USDT(TRC20 / ERC20)
//
// 后端做严格 regex 校验 + 30 天 ≥ 3 次风控;客端先做基础格式快速反馈即可。
export function useBindUsdtAddress() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      type: 'usdt_trc20' | 'usdt_erc20'
      address: string
    }): Promise<WalletPayoutAccount> => {
      return await apiClient.post<WalletPayoutAccount>('/gapi/agent/payout-accounts', input)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', 'payout_accounts'] }),
  })
}

// useBindBankCard P3-W7:绑银行卡
//
// 卡号明文上行(HTTPS;不入 DB / 不入 audit_log);server 算 bcrypt + 取 last4 后入库。
export function useBindBankCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      bank_name: string
      card_no: string
      holder_name: string
    }): Promise<WalletPayoutAccount> => {
      return await apiClient.post<WalletPayoutAccount>('/gapi/agent/payout-accounts', {
        ...input,
        type: 'bank_cn',
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', 'payout_accounts'] }),
  })
}

// useUnbindPayoutAccount P3-W7:软删(status=inactive + deleted_at)
export function useUnbindPayoutAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (account_id: string): Promise<{ ok: true }> => {
      return await apiClient.del<{ ok: true }>('/gapi/agent/payout-accounts/' + account_id)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', 'payout_accounts'] }),
  })
}

/** @deprecated 使用 useBindUsdtAddress / useBindBankCard;此 hook 走 supabase 直插 stub,已被 P3-W7 真 endpoint 取代 */
export function useBindPayoutAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      account_type: 'usdt_trc20' | 'usdt_erc20' | 'bank_cn'
      account_value: string
      account_name?: string
      extra?: Record<string, unknown>
    }) => {
      const sb = getSupabase()
      const { error } = await sb.from('wallet_payout_account').insert(input).select().maybeSingle()
      if (error) throw error
      return { ok: true } as const
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent', 'payout_accounts'] }),
  })
}

// ─── 改密(`wLOFS`)— /gapi/auth/change-password 已存在 ───
export function changePassword(input: { old_password: string; new_password: string }) {
  return apiClient.post('/gapi/agent/auth/change-password', input)
}

// ─── B7 直接 supabase 直读 fallback(避免新表落地前依赖 /gapi/* handler) ───
// 注:已有 useMyLoginHistory 走 server endpoint;此处只是为 B5 独立
// route 提供更直观的"无 backend 时空态"展示。

export function useWithdrawHistory(limit = 50) {
  return useQuery({
    queryKey: ['agent', 'withdraw', 'history', limit] as const,
    queryFn: async (): Promise<{ withdrawals: WithdrawRow[] }> => {
      const resp = await apiClient.get<WithdrawListResp>('/gapi/agent/withdrawals', {
        query: { limit },
      })
      const withdrawals = (resp.withdrawals ?? []).map((w) => ({
        id: w.id,
        currency: payoutMethodToCurrency(w.payout_method),
        amount: w.amount_coin,
        amount_coin: w.amount_coin,
        payout_method: w.payout_method,
        payout_contact: w.payout_contact,
        status: w.status,
        reject_reason: w.reject_reason,
        created_at: w.created_at,
        updated_at: w.updated_at,
      }))
      return { withdrawals }
    },
    staleTime: 30_000,
  })
}

// ──────────────────── 数据统计(任务 9) ────────────────────
//
// 后端：GET /gapi/agent/stats?days=&scope=self|subtree（agent role）。
// 返回 { today, series, breakdown? }；agent 侧无 breakdown。
//
// 趋势数据点(后端 stats.DayPoint)复用 @dogdisk/shared DayPoint 单一来源,避免 DTO 漂移。

export type StatsScope = 'self' | 'subtree'

export type AgentStatsResponse = {
  today: DayPoint
  series: DayPoint[]
}

export function useAgentStats(days = 30, scope: StatsScope = 'self') {
  return useQuery({
    queryKey: ['agent', 'stats', days, scope] as const,
    queryFn: () =>
      apiClient.get<AgentStatsResponse>('/gapi/agent/stats', {
        query: { days, scope },
      }),
    staleTime: 30_000,
  })
}
